/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

// TODO: Refactor to css module
import './MobileMenu.scss';

import { useSelector } from 'react-redux';
import {
    Button,
    Drawer,
    Heading,
    DividerNew,
    SearchActions,
    SecurityHelper,
    useBreakpoint,
    useFlags,
} from 'sarsaparilla';

import { getMenuAccountItems, getMenuSections, Logo } from 'site-kit';
import { SearchAutosuggest } from '../../search-autosuggest';

import { type CartSliceState, useTypedDispatch } from '../../redux';
import { MobileMenuSignInOrAccountPanel } from './MobileMenuSignInOrAccountPanel';
import type { MenuSectionLink } from './Navbar.types';
import { MOBILE_MENU_TRIGGER_ID } from './Navbar.utils';

const { submitSimpleSearch, trackRunSearch } = SearchActions;

const searchInputQueryParam = 'headerTextQuery';

interface MobileMenuProps {
    isOpen: boolean;
    onClose: () => void;
    isGuestAllowed?: boolean;
    isGuest: boolean;
}

export function MobileMenu({
    isOpen,
    onClose,
    isGuestAllowed,
    isGuest,
}: MobileMenuProps) {
    const { newAccountNavLinks } = useFlags();
    const searchAcrossUS = process.env?.HOMEPAGE_OPTIONS?.searchAcrossUS ?? false;
    const bounds = process.env?.SEARCH?.bounds ?? null;
    const isWidget = SecurityHelper.isWidgetSession();
    const { isMdAndDown } = useBreakpoint();
    const shouldDisplaySearchBar = isMdAndDown && !isWidget;

    const dispatch = useTypedDispatch();
    const numItemsInCart = useSelector(
        (state: { cart: CartSliceState }) => state.cart?.reservation_count || 0
    );

    const menuSections = isWidget ? [] : getMenuSections();
    const menuAccountItems = getMenuAccountItems(numItemsInCart, newAccountNavLinks);

    const getCurrentUrl = () => window.location.href;
    const handleButtonLinkClick = (link: MenuSectionLink) => {
        const { clickTagCategory, clickTagAction, inventoryTypes, uiFilters } = link;

        const gaEvent = {
            category: clickTagCategory,
            action: clickTagAction,
            label: getCurrentUrl(),
        };

        trackRunSearch(gaEvent, () => {
            dispatch(
                submitSimpleSearch(
                    '',
                    inventoryTypes,
                    null,
                    null,
                    uiFilters,
                    searchAcrossUS,
                    // @ts-ignore - inferred types are incorrect
                    bounds,
                    // @ts-ignore - inferred types are incorrect
                    'available'
                )
            );
        });
    };

    const renderMenuSections = () =>
        menuSections.map((section, idx) => (
            <div className="section-menu" key={idx}>
                <div className="section-menu-title-wrapper">
                    <Heading
                        appearance="h5Caps"
                        headingLevel={4}
                        className="section-menu-title"
                    >
                        {section.title}
                    </Heading>

                    <DividerNew />
                </div>

                {section.links.map((link, index) => {
                    const {
                        url,
                        ariaLabel,
                        title,
                        target,
                        clickTagCategory,
                        clickTagAction,
                        inventoryTypes,
                    } = link;

                    if (inventoryTypes) {
                        return (
                            <Button
                                isUnstyled
                                key={index}
                                className="section-menu-link"
                                aria-label={ariaLabel}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleButtonLinkClick(link);
                                }}
                            >
                                {title}
                            </Button>
                        );
                    }

                    return (
                        <Button
                            isUnstyled
                            key={index}
                            clickTagCategory={clickTagCategory}
                            clickTagAction={clickTagAction}
                            clickTagLabel={getCurrentUrl()}
                            href={url}
                            className="section-menu-link"
                            aria-label={ariaLabel}
                            target={target}
                            rel="noopener noreferrer"
                        >
                            {title}
                        </Button>
                    );
                })}
            </div>
        ));

    return (
        <Drawer
            label="Mobile menu"
            isLabelVisible={false}
            isOpen={isOpen}
            onClose={onClose}
            triggerId={MOBILE_MENU_TRIGGER_ID}
            direction="right"
            appearance="dark"
            hasRecLogo
            logoElement={<Logo />}
            darkModeLogoElement={<Logo isLight />}
        >
            <div className="nav-mobile-menu-body">
                {shouldDisplaySearchBar ? (
                    <div className="form-item-wrap">
                        <SearchAutosuggest
                            size="lg"
                            queryCriteriaPropertyName={searchInputQueryParam}
                            id="mobile-menu-search-input"
                            popupClassName="mobile-menu-search-popup"
                        />
                    </div>
                ) : null}

                <MobileMenuSignInOrAccountPanel
                    menuAccountItems={menuAccountItems}
                    isGuestAllowed={isGuestAllowed}
                    isGuest={isGuest}
                />

                {renderMenuSections()}
            </div>
        </Drawer>
    );
}
